import { pageFixture, sliceFixture } from './fixtures.js';
import { isolationMatrixFixture } from './fixtures/isolation-matrix.js';
import { concurrencySequenceFixture } from './fixtures/concurrency-sequence.js';
import { memoryLayoutFixture } from './fixtures/memory-layout.js';
import { queryPlanFixture } from './fixtures/query-plan.js';
import { latencyDistributionFixture, latencyGuidedTargets } from './fixtures/latency-distribution.js';
import { stackFramesFixture, stackGuidedTargets } from './fixtures/stack-frames.js';
import { complexityArgumentFixture, complexityGuidedTargets } from './fixtures/complexity-argument.js';
import { executionArtifactFixture, executionArtifactGuidedTargets } from './fixtures/execution-artifact.js';
import { btreeFixture, btreeGuidedLookupTargets } from './fixtures/btree.js';
import { protocolStateMachineFixture, protocolGuidedTargets } from './fixtures/protocol-state-machine.js';
import type { FigureSpec } from './model.js';
import { queueSelectTargets, queueViewAtCapacity, queueViewMomentFixture } from './fixtures/queue-view.js';

export interface SemanticCheckpoint {
  id: string;
  label: string;
  explanation: string;
  prompt?: string;
  expectedSemanticId?: string;
  spec: FigureSpec;
}

export interface TransitionProgram {
  id: string;
  family: FigureSpec['family'];
  title: string;
  code: string;
  outcome: string;
  checkpoints: SemanticCheckpoint[];
}

export type TransitionVariant = 'primary' | 'reallocate';

export function transitionFor(family: FigureSpec['family'], width: number, variant: TransitionVariant = 'primary'): TransitionProgram {
  if (family === 'slice-header' && variant === 'reallocate') return sliceReallocateTransition(width);
  if (family === 'slice-header') return sliceAppendTransition(width);
  if (family === 'database-page') return pageInsertTransition(width);
  if (family === 'isolation-matrix') return isolationGuaranteeTransition(width);
  if (family === 'concurrency-sequence') return concurrencyHandoffTransition(width);
  if (family === 'memory-layout') return memoryLayoutTransition(width);
  if (family === 'query-plan') return queryPlanTransition(width);
  if (family === 'latency-distribution') return latencyDistributionTransition(width);
  if (family === 'stack-frames') return stackFramesTransition(width);
  if (family === 'complexity-argument') return complexityArgumentTransition(width);
  if (family === 'execution-artifact') return executionArtifactTransition(width);
  if (family === 'btree') return btreeLookupTransition(width);
  if (family === 'protocol-state-machine') return protocolStateMachineTransition(width);
  if (family === 'queue-view') return queueViewTransition(width);
  throw new Error('transition: family is not registered');
}

function protocolStateMachineTransition(width: number): TransitionProgram {
  const base = protocolStateMachineFixture(width, false, 'overview');
  return {
    id: 'connection-protocol.v1', family: 'protocol-state-machine', title: 'A connection changes only along legal edges', code: 'conn.connect()',
    outcome: 'The retry and failure paths are explicit facts of the protocol, not decorative animation.',
    checkpoints: [
      { id: 'connect', label: 'Begin connection', explanation: 'Idle has one declared event that starts a connection.', prompt: 'Tap the connect transition.', expectedSemanticId: protocolGuidedTargets.connect, spec: { ...base, focus: protocolGuidedTargets.connect } },
      { id: 'failure', label: 'Observe failure', explanation: 'A timeout moves the connection to Failed; no hidden state is invented.', prompt: 'Tap Failed.', expectedSemanticId: protocolGuidedTargets.failed, spec: { ...base, focus: protocolGuidedTargets.failed } },
    ],
  };
}

function sliceAppendTransition(width: number): TransitionProgram {
  const base = sliceFixture(width, false, 'overview');
  const stagedValues = [...base.values];
  stagedValues[base.len] = 88;

  return {
    id: 'slice.append-with-capacity.v1',
    family: 'slice-header',
    title: 'Append without reallocating',
    code: 'items = append(items, 88)',
    outcome: 'The value lands in owned capacity, then len commits the new visible extent.',
    checkpoints: [
      {
        id: 'ready',
        label: 'Find slot',
        explanation: `len is ${base.len}; cap is ${base.cap}. The backing array already owns two spare words.`,
        prompt: 'Tap the exact cell where append writes 88.',
        expectedSemanticId: `array.cell.${base.len}`,
        spec: { ...base, id: 'slice-append-ready', focus: 'extent' },
      },
      {
        id: 'write',
        label: 'Write value',
        explanation: '88 is staged in the next owned cell. It exists physically, but the slice length has not committed it yet.',
        prompt: 'Tap LEN to make the new element visible to the program.',
        expectedSemanticId: 'slice.len',
        spec: { ...base, id: 'slice-append-write', values: stagedValues, focus: 'overview', emphasis: [`array.cell.${base.len}`] },
      },
      {
        id: 'commit',
        label: 'Commit len',
        explanation: 'len advances from 4 to 5. Pointer and capacity stay unchanged: no allocation and no copy.',
        spec: { ...base, id: 'slice-append-commit', values: stagedValues, len: base.len + 1, focus: 'extent', emphasis: [`array.cell.${base.len}`] },
      },
    ],
  };
}

function sliceReallocateTransition(width: number): TransitionProgram {
  const base = {
    ...sliceFixture(width, false, 'overview'),
    id: 'slice-grow-ready',
    len: 4,
    cap: 4,
    targetIndex: 0,
    values: [100, 42, 7, 3],
    pointer: '0xc0…a0',
    pointerAddress: '0xc0000140a0',
  };
  const emptyAllocation = [null, null, null, null, null, null, null, null];
  const copiedValues = [100, 42, 7, 3, null, null, null, null];
  const appendedValues = [100, 42, 7, 3, 88, null, null, null];

  return {
    id: 'slice.append-reallocate.v1',
    family: 'slice-header',
    title: 'Append after capacity is exhausted',
    code: 'full = append(full, 88)',
    outcome: 'A larger backing array is allocated, old values are copied, the pointer changes, then len commits 88.',
    checkpoints: [
      {
        id: 'full',
        label: 'Detect full',
        explanation: 'len equals cap, so there is no owned cell available for the next value.',
        prompt: 'Tap the header word that proves an in-place append is impossible.',
        expectedSemanticId: 'slice.cap',
        spec: { ...base, focus: 'extent' },
      },
      {
        id: 'allocate',
        label: 'Allocate',
        explanation: 'The runtime allocates eight cells at a new address. The pointer changes before the data is copied.',
        prompt: 'Tap the first destination cell where copying begins.',
        expectedSemanticId: 'array.cell.0',
        spec: { ...base, id: 'slice-grow-allocate', cap: 8, values: emptyAllocation, pointer: '0xc0…00', pointerAddress: '0xc000020000', focus: 'pointer', emphasis: ['slice.cap'] },
      },
      {
        id: 'copy',
        label: 'Copy values',
        explanation: 'The four visible values are copied into the new allocation. Four spare cells remain owned but outside len.',
        prompt: 'Tap the next spare cell where append can finally write 88.',
        expectedSemanticId: 'array.cell.4',
        spec: { ...base, id: 'slice-grow-copy', cap: 8, values: copiedValues, pointer: '0xc0…00', pointerAddress: '0xc000020000', focus: 'pointer', emphasis: ['array.cell.0', 'array.cell.1', 'array.cell.2', 'array.cell.3'] },
      },
      {
        id: 'commit',
        label: 'Commit len',
        explanation: '88 is written and len advances to five. The final slice now describes the new eight-cell allocation.',
        spec: { ...base, id: 'slice-grow-commit', cap: 8, len: 5, values: appendedValues, pointer: '0xc0…00', pointerAddress: '0xc000020000', targetIndex: 0, focus: 'extent', emphasis: ['array.cell.4'] },
      },
    ],
  };
}

function pageInsertTransition(width: number): TransitionProgram {
  const base = pageFixture(width, false, 'overview');
  const inserted = { id: 'customer-005', label: 'customer #005', bytes: 608 };
  const after = { ...base, tuples: [...base.tuples, inserted] };

  return {
    id: 'page.insert-tuple.v1',
    family: 'database-page',
    title: 'Insert one tuple',
    code: 'INSERT customer #005 · 608 B',
    outcome: 'One slot grows downward while the tuple payload grows upward; the remaining gap is still contiguous.',
    checkpoints: [
      {
        id: 'ready',
        label: 'Find space',
        explanation: 'The page has one contiguous free-space band between the slot directory and packed tuples.',
        prompt: 'Tap the region the insert must consume.',
        expectedSemanticId: 'page.free-space',
        spec: { ...base, id: 'page-insert-ready', focus: 'overview' },
      },
      {
        id: 'write',
        label: 'Write tuple',
        explanation: 'The new record is packed from the page end and a fifth slot is reserved at the top.',
        prompt: 'Tap the new slot-directory entry that locates customer #005.',
        expectedSemanticId: 'slot.customer-005',
        spec: { ...after, id: 'page-insert-write', focus: 'tuple', focusTupleId: inserted.id, emphasis: ['page.free-space'] },
      },
      {
        id: 'commit',
        label: 'Commit page',
        explanation: 'The slot and tuple now form one locates relation. The free-space boundary moved, but page size did not.',
        spec: { ...after, id: 'page-insert-commit', focus: 'tuple', focusTupleId: inserted.id },
      },
    ],
  };
}

function isolationGuaranteeTransition(width: number): TransitionProgram {
  const readCommitted = 'matrix.cell.read-committed.dirty-read';
  const depends = 'matrix.cell.repeatable-read.phantom-read';
  const serializable = 'matrix.cell.serializable.write-skew';
  return {
    id: 'isolation.compare-guarantees.v1',
    family: 'isolation-matrix',
    title: 'Choose the guarantee, not the label',
    code: 'BEGIN TRANSACTION ISOLATION LEVEL …',
    outcome: 'Isolation levels become a set of explicit anomaly guarantees, including the engine-dependent boundary.',
    checkpoints: [
      {
        id: 'committed',
        label: 'Committed',
        explanation: 'Read committed rejects values another transaction has not committed, but it does not make consecutive statements see one stable world.',
        prompt: 'Tap the cell proving read committed blocks dirty reads.',
        expectedSemanticId: readCommitted,
        spec: isolationMatrixFixture(width, false, readCommitted),
      },
      {
        id: 'dependent',
        label: 'Engine boundary',
        explanation: 'Repeatable read has implementation-specific phantom semantics. The compiler must preserve uncertainty rather than invent certainty.',
        prompt: 'Tap the repeatable-read guarantee that depends on the database engine.',
        expectedSemanticId: depends,
        spec: isolationMatrixFixture(width, false, depends),
      },
      {
        id: 'serial',
        label: 'Serializable',
        explanation: 'Serializable prevents write skew by requiring an outcome equivalent to some valid one-at-a-time ordering.',
        spec: isolationMatrixFixture(width, false, serializable),
      },
    ],
  };
}

function concurrencyHandoffTransition(width: number): TransitionProgram {
  return {
    id: 'concurrency.channel-handoff.v1',
    family: 'concurrency-sequence',
    title: 'Follow one job across concurrency boundaries',
    code: 'jobs <- job  ·  result := <-done',
    outcome: 'Stable participant lanes expose the causal order from request, through channel and worker, to the database and back.',
    checkpoints: [
      {
        id: 'submit',
        label: 'Submit',
        explanation: 'The request hands a job to the channel. Logical order is explicit even if wall-clock timing changes.',
        prompt: 'Tap the first hand-off from the request to the jobs channel.',
        expectedSemanticId: 'event.submit',
        spec: concurrencySequenceFixture(width, false, 1, 'submit'),
      },
      {
        id: 'database',
        label: 'Persist',
        explanation: 'The worker reaches storage only after receiving the job; the event sequence records that causal dependency.',
        prompt: 'Tap the database update sent by the worker.',
        expectedSemanticId: 'event.update',
        spec: concurrencySequenceFixture(width, false, 4, 'update'),
      },
      {
        id: 'resume',
        label: 'Resume',
        explanation: 'The result returns only after the database acknowledges the commit. The entire causal chain is now visible.',
        spec: concurrencySequenceFixture(width, false, 6, 'resume'),
      },
    ],
  };
}

function memoryLayoutTransition(width: number): TransitionProgram {
  return {
    id: 'memory.explain-padding.v1',
    family: 'memory-layout',
    title: 'Account for every byte in a struct',
    code: 'unsafe.Sizeof(AccountSnapshot{})',
    outcome: 'The struct occupies 24 bytes because a one-byte flag is followed by seven explicit alignment bytes before the next word.',
    checkpoints: [
      {
        id: 'flag',
        label: 'Find the flag',
        explanation: 'enabled needs only one byte. Its logical simplicity does not remove the alignment needs of the field after it.',
        prompt: 'Tap the one-byte enabled field.',
        expectedSemanticId: 'memory.segment.enabled',
        spec: memoryLayoutFixture(width, false, 'memory.segment.enabled'),
      },
      {
        id: 'padding',
        label: 'Explain the gap',
        explanation: 'Seven bytes align balanceCents to the next eight-byte boundary. The gap is real storage, not empty drawing space.',
        prompt: 'Tap the bytes inserted only for alignment.',
        expectedSemanticId: 'memory.segment.alignment-gap',
        spec: memoryLayoutFixture(width, false, 'memory.segment.alignment-gap'),
      },
      {
        id: 'accounted',
        label: 'Accounted',
        explanation: 'Every field and padding segment now has an exact offset, address, byte extent, and semantic identity.',
        spec: memoryLayoutFixture(width, false, 'memory.segment.balance'),
      },
    ],
  };
}

function queryPlanTransition(width: number): TransitionProgram {
  return {
    id: 'query-plan.follow-rows.v1',
    family: 'query-plan',
    title: 'Follow rows from scans to the result',
    code: 'EXPLAIN SELECT sum(revenue) …',
    outcome: 'Rows enter through scans, combine at the join, and collapse to one aggregate result. Cost and cardinality remain attached to the responsible operator.',
    checkpoints: [
      {
        id: 'source',
        label: 'Source rows',
        explanation: 'The orders scan introduces the largest candidate stream into this plan.',
        prompt: 'Tap the operator that reads recent orders.',
        expectedSemanticId: 'plan.node.scan-orders',
        spec: queryPlanFixture(width, false, 'scan-orders'),
      },
      {
        id: 'combine',
        label: 'Combine',
        explanation: 'The hash join merges orders with active customers and changes both row meaning and cardinality.',
        prompt: 'Tap the operator where both input streams meet.',
        expectedSemanticId: 'plan.node.join-customer',
        spec: queryPlanFixture(width, false, 'join-customer'),
      },
      {
        id: 'result',
        label: 'Result',
        explanation: 'The root aggregate consumes the joined stream and produces one revenue result.',
        spec: queryPlanFixture(width, false, 'sum-revenue'),
      },
    ],
  };
}

function latencyDistributionTransition(width: number): TransitionProgram {
  return {
    id: 'latency.read-the-tail.v1',
    family: 'latency-distribution',
    title: 'Read the distribution beyond the average',
    code: 'observe(request_duration_ms)',
    outcome: 'The common request, the p95 tail, and the comparison series are separate semantic facts instead of one misleading average.',
    checkpoints: [
      {
        id: 'body',
        label: 'Typical request',
        explanation: 'p50 divides the observations in half and locates the body of the distribution.',
        prompt: 'Tap the current release p50 marker.',
        expectedSemanticId: latencyGuidedTargets.body,
        spec: latencyDistributionFixture(width, false, 'body', true),
      },
      {
        id: 'tail',
        label: 'Tail cost',
        explanation: 'p95 exposes the slow experience hidden by the dense body of fast requests.',
        prompt: 'Tap the current release p95 marker.',
        expectedSemanticId: latencyGuidedTargets.tail,
        spec: latencyDistributionFixture(width, false, 'tail', true),
      },
      {
        id: 'compare',
        label: 'Compare',
        explanation: 'The optimized candidate shifts the distribution, while the explicit percentile marks keep tail trade-offs visible.',
        spec: latencyDistributionFixture(width, false, 'comparison', true),
      },
    ],
  };
}

function stackFramesTransition(width: number): TransitionProgram {
  return {
    id: 'stack.call-and-return.v1',
    family: 'stack-frames',
    title: 'Watch one call push and release a frame',
    code: 'account, err := store.loadAccount(id)',
    outcome: 'The callee receives its own locals, occupies the deepest frame, then returns control while the caller keeps its identity and position.',
    checkpoints: [
      {
        id: 'caller',
        label: 'Caller active',
        explanation: 'serveHTTP owns the request locals and is currently the deepest active frame.',
        prompt: 'Tap the frame that is about to call the store.',
        expectedSemanticId: stackGuidedTargets.entry,
        spec: stackFramesFixture(width, false, 'entry', 'serve-http'),
      },
      {
        id: 'push',
        label: 'Push callee',
        explanation: 'loadAccount receives a new frame and its own locals. Isometric depth now carries the literal call depth.',
        prompt: 'Tap the newly pushed callee frame.',
        expectedSemanticId: stackGuidedTargets.push,
        spec: stackFramesFixture(width, false, 'call', 'load-account'),
      },
      {
        id: 'return',
        label: 'Return',
        explanation: 'The callee is shown as returned while serveHTTP becomes active again; caller geometry never jumps.',
        spec: stackFramesFixture(width, false, 'return', 'serve-http'),
      },
    ],
  };
}

function complexityArgumentTransition(width: number): TransitionProgram {
  return {
    id: 'complexity.derive-triangular-loop.v1',
    family: 'complexity-argument',
    title: 'Build the conclusion one justified step at a time',
    code: 'for i := 1; i <= n; i++ { for j := 1; j <= i; j++ { work() } }',
    outcome: 'The quadratic bound is supported by an exact operation count, a series identity, and an explicit dominant-term argument.',
    checkpoints: [
      { id: 'count', label: 'Count work', explanation: 'Outer iteration i performs exactly i inner operations.', prompt: 'Tap the step that translates the loop into a summation.', expectedSemanticId: complexityGuidedTargets.given, spec: complexityArgumentFixture(width, false, 'count-iterations') },
      { id: 'identity', label: 'Close the sum', explanation: 'The first-n-integers identity turns the summation into an exact polynomial.', prompt: 'Tap the step that applies the series identity.', expectedSemanticId: complexityGuidedTargets.identity, spec: complexityArgumentFixture(width, false, 'apply-series-identity') },
      { id: 'conclusion', label: 'Conclude', explanation: 'The quadratic term dominates after constant factors and lower-order terms are removed.', spec: complexityArgumentFixture(width, false, 'identify-dominant-term') },
    ],
  };
}

function executionArtifactTransition(width: number): TransitionProgram {
  return {
    id: 'artifact.read-explain-evidence.v1',
    family: 'execution-artifact',
    title: 'Interrogate the system output without rewriting it',
    code: 'EXPLAIN (ANALYZE, BUFFERS) SELECT …',
    outcome: 'The command, discarded-row evidence, and measured runtime remain verbatim and traceable to exact artifact lines.',
    checkpoints: [
      { id: 'command', label: 'Invocation', explanation: 'The artifact begins with the exact command that produced the evidence.', prompt: 'Tap the command that generated this plan.', expectedSemanticId: executionArtifactGuidedTargets.command, spec: executionArtifactFixture(width, false, executionArtifactGuidedTargets.command) },
      { id: 'filter', label: 'Discarded rows', explanation: 'Rows Removed by Filter exposes work that did not survive the predicate.', prompt: 'Tap the line proving how many rows were discarded.', expectedSemanticId: executionArtifactGuidedTargets.filterCost, spec: executionArtifactFixture(width, false, executionArtifactGuidedTargets.filterCost) },
      { id: 'runtime', label: 'Measured runtime', explanation: 'Execution Time is observed system evidence, not a planner estimate.', spec: executionArtifactFixture(width, false, executionArtifactGuidedTargets.totalRuntime) },
    ],
  };
}

function btreeLookupTransition(width: number): TransitionProgram {
  const finalSpec = btreeFixture(width, false, 'leaf-d');
  return {
    id: 'btree.lookup-62.v1',
    family: 'btree',
    title: 'Discard key ranges until one record remains',
    code: 'index.lookup(62)',
    outcome: 'Ordered separators route 62 through two child ranges to one leaf key and its row record.',
    checkpoints: [
      { id: 'root', label: 'Compare root', explanation: '62 is greater than the root separator 40, so the entire left subtree is discarded.', prompt: 'Tap the root node where lookup starts.', expectedSemanticId: btreeGuidedLookupTargets[0], spec: btreeFixture(width, false, 'root') },
      { id: 'range', label: 'Choose range', explanation: 'The ≥40 edge is a semantic range decision, not a decorative connector.', prompt: 'Tap the child-range edge selected by key 62.', expectedSemanticId: btreeGuidedLookupTargets[1], spec: btreeFixture(width, false, 'branch-right') },
      { id: 'leaf', label: 'Verify key', explanation: 'After the ≥60 decision, the leaf contains an exact key 62 entry.', prompt: 'Tap key 62 inside the destination leaf.', expectedSemanticId: btreeGuidedLookupTargets[4], spec: btreeFixture(width, false, 'leaf-d') },
      { id: 'record', label: 'Resolve record', explanation: 'The leaf key resolves to row:062; the record evidence is now the active conclusion.', spec: { ...finalSpec, emphasis: [btreeGuidedLookupTargets[5]] } },
    ],
  };
}

function queueViewTransition(width: number): TransitionProgram {
  const overdue = `${queueSelectTargets.crossing}.overdue`;
  return {
    id: 'queue.age-against-the-deadline.v1',
    family: 'queue-view',
    title: 'Watch the queue age past the client timeout',
    code: 'pool.Acquire(ctx) // 8 waiters, 2 checkouts/s',
    outcome: 'The same eight checkouts get older against a fixed deadline; raising the buffer only adds more waiting.',
    checkpoints: [
      {
        id: 'standing',
        label: 'Queue stands full',
        explanation: 'Eight waiters, two checkouts a second. The head has waited 2 000 ms against a 1900 ms client timeout, so its answer is already unwanted.',
        prompt: 'Tap the wait that is already past the client timeout.',
        expectedSemanticId: queueSelectTargets.head,
        spec: queueViewMomentFixture(width, 'standing'),
      },
      {
        id: 'crossing',
        label: 'One more crosses',
        explanation: '200 ms later the pool has still not finished a checkout, and GET /orders/9014 crosses the same line. Nothing was served; one more answer became worthless.',
        prompt: 'Tap the part of that wait which is past the deadline.',
        expectedSemanticId: overdue,
        spec: queueViewMomentFixture(width, 'crossing'),
      },
      {
        id: 'compounding',
        label: 'Three are dead',
        explanation: 'Another 200 ms: three of eight answers are now unwanted before they are computed. The deadline has not moved once — only the waits grew.',
        prompt: 'Tap the answers nobody is waiting for any more.',
        expectedSemanticId: queueSelectTargets.doomed,
        spec: queueViewMomentFixture(width, 'compounding'),
      },
      {
        id: 'bigger-buffer',
        label: 'A bigger buffer',
        explanation: 'Settled at eight slots, every waiter sits behind 8 ÷ 2 = 4 000 ms of queue and five of eight are dead on arrival. The buffer bought waiting, not throughput; the pool still clears two per second and still sheds three.',
        spec: queueViewAtCapacity(width, 8),
      },
    ],
  };
}
